import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Crown, Loader2, Search, ShieldCheck, Clock, RefreshCw, ArrowLeft, Send, Lightbulb, MessageSquare } from "lucide-react";

import { AdminShell, StatCard } from "@/components/admin/AdminShell";
import { adminService } from "@/services/adminService";
import { publicService } from "@/services/publicService";
import { fmtDate, fmtDateTime } from "@/lib/datetime";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";

const Loading = () => <div className="py-16 text-center text-sm text-muted-foreground"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></div>;
const Table = ({ head, children }) => (
  <div className="overflow-x-auto rounded-xl border border-border bg-white">
    <table className="w-full text-sm"><thead className="bg-secondary/60 text-left text-xs uppercase tracking-wide text-muted-foreground">
      <tr>{head.map((h) => <th key={h} className="px-4 py-2.5 font-semibold">{h}</th>)}</tr></thead>
      <tbody>{children}</tbody></table>
  </div>
);
const StatusBadge = ({ s }) => <Badge variant="outline" className="capitalize">{(s || "none").replace(/_/g, " ")}</Badge>;

/* ---------------- Command Center ---------------- */
export function AdminCommandCenter() {
  const [d, setD] = useState(null);
  useEffect(() => { adminService.commandCenter().then(setD).catch(() => toast.error("Load failed")); }, []);
  return (
    <AdminShell title="Command Center" description="Live operational snapshot">
      {!d ? <Loading /> : (
        <div className="space-y-6" data-testid="admin-command-center">
          <div className="rounded-xl border border-brand-gold/40 bg-brand-navy p-5 text-white">
            <p className="text-[11px] font-bold uppercase tracking-widest text-brand-soft-gold">Executive Brief</p>
            <p className="mt-1.5 text-sm leading-relaxed" data-testid="exec-brief">{d.executive_brief}</p>
          </div>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4">
            <StatCard label="Workspaces" value={d.metrics.total_workspaces} accent />
            <StatCard label="Active Paid" value={d.metrics.active_paid} />
            <StatCard label="MRR" value={d.metrics.mrr} sub={`ARR ${d.metrics.arr}`} accent />
            <StatCard label="Weekly Active" value={d.metrics.weekly_active_businesses} sub="WAB (7d)" />
            <StatCard label="Trials" value={d.metrics.trials} />
            <StatCard label="Complimentary" value={d.metrics.complimentary} />
            <StatCard label="Founding" value={`${d.metrics.founding_agents}/100`} sub={`${d.metrics.founding_remaining} left`} />
            <StatCard label="In Recovery" value={d.metrics.past_due + d.metrics.grace_period} sub="past due + grace" />
          </div>
          <p className="text-xs text-muted-foreground">Complimentary accounts and trials are never counted as revenue. {d.metrics.support_tickets_note}</p>
        </div>
      )}
    </AdminShell>
  );
}

/* ---------------- Customers ---------------- */
export function AdminCustomers() {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [alphaOnly, setAlphaOnly] = useState(false);
  const [detail, setDetail] = useState(null);
  const [isSuper, setIsSuper] = useState(false);

  const load = useCallback(async () => {
    const params = {};
    if (q) params.q = q;
    if (alphaOnly) params.cohort = "private_alpha";
    setRows((await adminService.customers(params)).customers);
  }, [q, alphaOnly]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { adminService.context().then((c) => setIsSuper(c.is_super_admin)).catch(() => {}); }, []);

  return (
    <AdminShell title="Customers" description="Operational account overview">
      <div className="mb-4 flex gap-2">
        <div className="relative flex-1 max-w-sm"><Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, workspace…" className="pl-8" data-testid="admin-customer-search" /></div>
        <Button variant={alphaOnly ? "default" : "outline"} size="sm" onClick={() => setAlphaOnly((v) => !v)}
          className={alphaOnly ? "bg-brand-navy text-white" : ""} data-testid="admin-alpha-filter">Private Alpha</Button>
      </div>
      {!rows ? <Loading /> : (
        <Table head={["Workspace", "Owner", "Status", "Plan", "Founding", "Access"]}>
          {rows.map((r) => (
            <tr key={r.workspace_id} className="cursor-pointer border-t border-border hover:bg-secondary/40" onClick={() => setDetail(r.workspace_id)} data-testid="admin-customer-row">
              <td className="px-4 py-2.5 font-medium text-foreground">
                {r.workspace_name}
                {r.cohort === "private_alpha" && <Badge className="ml-2 bg-brand-gold/20 text-brand-navy" data-testid="admin-alpha-badge">Alpha</Badge>}
              </td>
              <td className="px-4 py-2.5 text-muted-foreground">{r.owner_email || "—"}</td>
              <td className="px-4 py-2.5"><StatusBadge s={r.status} /></td>
              <td className="px-4 py-2.5 text-muted-foreground">{r.plan_code || "—"}</td>
              <td className="px-4 py-2.5">{r.founding_agent ? <Badge className="bg-brand-navy text-white"><Crown className="mr-1 h-3 w-3 text-brand-gold" />#{String(r.founding_number).padStart(3, "0")}</Badge> : "—"}</td>
              <td className="px-4 py-2.5">{r.access ? <span className="text-emerald-600">Active</span> : <span className="text-brand-burgundy">No access</span>}</td>
            </tr>
          ))}
        </Table>
      )}
      {detail && <CustomerDetail workspaceId={detail} isSuper={isSuper} onClose={() => setDetail(null)} onChanged={load} />}
    </AdminShell>
  );
}

function CustomerDetail({ workspaceId, isSuper, onClose, onChanged }) {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const [reason, setReason] = useState("");
  const load = useCallback(() => adminService.customer(workspaceId).then(setD), [workspaceId]);
  useEffect(() => { load(); }, [load]);

  const act = async (fn, msg) => { setBusy(true); try { await fn(); toast.success(msg); await load(); onChanged?.(); } catch (e) { toast.error(e?.message); } finally { setBusy(false); } };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="admin-customer-detail">
        <DialogHeader><DialogTitle>{d?.identity?.workspace_name || "Customer"}</DialogTitle>
          <DialogDescription>Operational account overview. Customer CRM, notes, and documents are not shown here.</DialogDescription>
        </DialogHeader>
        {!d ? <Loading /> : (
          <div className="space-y-3 text-sm">
            <Field label="Owner" value={`${d.identity.owner_name || "—"} · ${d.identity.owner_email || ""}`} />
            <Field label="Created" value={fmtDate(d.identity.created_at)} />
            <Field label="Business" value={d.business_profile.business_name || (d.business_profile.complete ? "—" : "Profile incomplete")} />
            <Field label="Subscription" value={<StatusBadge s={d.subscription.status} />} />
            {d.subscription.founding_agent && <Field label="Founding" value={`#${String(d.subscription.founding_number).padStart(3, "0")} · ${d.subscription.founding_rate_locked ? "rate locked" : "rate lost"}`} />}
            <Field label="Last activity" value={d.last_activity_at ? fmtDateTime(d.last_activity_at) : "—"} />
            <Field label="AI usage" value={`${d.ai_usage.requests} req · ${d.ai_usage.estimated_cost}`} />
            {d.onboarding && <Field label="Onboarding" value={`${(d.onboarding.status || "").replace(/_/g, " ")} · ${d.onboarding.completed_count}/${d.onboarding.total_steps} (${d.onboarding.percent}%)`} />}
            <p className="rounded-md bg-secondary/60 p-2 text-xs text-muted-foreground">{d.note}</p>
            {isSuper && (
              <div className="space-y-2 border-t border-border pt-3">
                <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (required for sensitive actions)…" rows={2} data-testid="admin-action-reason" />
                <div className="flex flex-wrap gap-2">
                  {!d.subscription.complimentary
                    ? <Button size="sm" disabled={busy || !reason} onClick={() => act(() => adminService.grantComplimentary(workspaceId, reason), "Complimentary granted")} data-testid="admin-grant-comp">Grant complimentary</Button>
                    : <Button size="sm" variant="outline" disabled={busy} onClick={() => act(() => adminService.revokeComplimentary(workspaceId, reason || "revoke"), "Complimentary revoked")} data-testid="admin-revoke-comp">Revoke complimentary</Button>}
                  {!d.subscription.admin_suspended
                    ? <Button size="sm" variant="ghost" className="text-brand-burgundy" disabled={busy || !reason} onClick={() => act(() => adminService.suspend(workspaceId, reason), "Access suspended")} data-testid="admin-suspend">Suspend access</Button>
                    : <Button size="sm" variant="outline" disabled={busy} onClick={() => act(() => adminService.restore(workspaceId), "Access restored")} data-testid="admin-restore">Restore access</Button>}
                  <Button size="sm" variant="outline" disabled={busy || !reason} onClick={() => act(() => adminService.grantSupportAccess(workspaceId, reason, 60), "Temporary support access granted (60 min)")} data-testid="admin-grant-support">Grant support access</Button>
                </div>
              </div>
            )}
          </div>
        )}
        <DialogFooter><Button variant="outline" onClick={onClose}>Close</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
const Field = ({ label, value }) => (
  <div className="flex items-start justify-between gap-3 border-b border-border py-1.5 last:border-0">
    <span className="text-muted-foreground">{label}</span><span className="text-right font-medium text-foreground">{value}</span>
  </div>
);

/* ---------------- Founding ---------------- */
export function AdminFounding() {
  const [d, setD] = useState(null);
  useEffect(() => { adminService.founding().then(setD).catch(() => toast.error("Load failed")); }, []);
  return (
    <AdminShell title="Founding Agents" description="First 100 qualifying activations">
      {!d ? <Loading /> : (
        <div className="space-y-5" data-testid="admin-founding">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <StatCard label="Cap" value={d.cap} /><StatCard label="Used" value={d.used} accent />
            <StatCard label="Remaining" value={d.remaining} /><StatCard label="Enrollment" value={d.enrollment_open ? "Open" : "Closed"} />
          </div>
          <Table head={["#", "Workspace", "Status", "Rate", "Activated"]}>
            {d.agents.length === 0 ? <tr><td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">No Founding Agents yet.</td></tr>
              : d.agents.map((a) => (
                <tr key={a.founding_number} className="border-t border-border">
                  <td className="px-4 py-2.5 font-bold text-brand-navy">#{String(a.founding_number).padStart(3, "0")}</td>
                  <td className="px-4 py-2.5">{a.workspace_name}</td>
                  <td className="px-4 py-2.5"><StatusBadge s={a.status} /></td>
                  <td className="px-4 py-2.5">{a.rate_locked ? <span className="text-emerald-600">Locked $19</span> : <span className="text-muted-foreground">Lost</span>}</td>
                  <td className="px-4 py-2.5 text-muted-foreground">{fmtDate(a.activated_at)}</td>
                </tr>))}
          </Table>
        </div>
      )}
    </AdminShell>
  );
}

/* ---------------- Subscriptions ---------------- */
export function AdminSubscriptions() {
  const [rows, setRows] = useState(null);
  const [liveBilling, setLiveBilling] = useState(true);
  useEffect(() => { adminService.subscriptions().then((d) => setRows(d.subscriptions)).catch(() => toast.error("Load failed")); }, []);
  useEffect(() => { publicService.siteConfig().then((c) => setLiveBilling(c.live_billing)).catch(() => {}); }, []);
  return (
    <AdminShell title="Subscriptions" description={`Revenue & lifecycle${liveBilling ? "" : " (Stripe test mode)"}`}>
      {!rows ? <Loading /> : (
        <Table head={["Workspace", "Plan", "Status", "Monthly", "Period ends"]}>
          {rows.map((r) => (
            <tr key={r.id} className="border-t border-border">
              <td className="px-4 py-2.5 text-xs text-muted-foreground">{r.workspace_id?.slice(-6)}</td>
              <td className="px-4 py-2.5">{r.plan_code || "—"}</td>
              <td className="px-4 py-2.5"><StatusBadge s={r.status} /></td>
              <td className="px-4 py-2.5 font-medium">{r.monthly_value}</td>
              <td className="px-4 py-2.5 text-muted-foreground">{r.current_period_end ? fmtDate(r.current_period_end) : "—"}</td>
            </tr>))}
        </Table>
      )}
    </AdminShell>
  );
}

/* ---------------- Analytics ---------------- */
export function AdminAnalytics() {
  const [d, setD] = useState(null);
  useEffect(() => { adminService.commandCenter().then((x) => setD(x.metrics)).catch(() => toast.error("Load failed")); }, []);
  return (
    <AdminShell title="Product Analytics" description="Factual platform metrics">
      {!d ? <Loading /> : (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4" data-testid="admin-analytics">
          <StatCard label="Weekly Active Businesses" value={d.weekly_active_businesses} accent sub="≥1 meaningful activity / 7d" />
          <StatCard label="Workspaces" value={d.total_workspaces} />
          <StatCard label="Paid" value={d.active_paid} /><StatCard label="Trials" value={d.trials} />
          <StatCard label="Complimentary" value={d.complimentary} /><StatCard label="Founding" value={d.founding_agents} />
          <StatCard label="Cancel-at-end" value={d.cancel_at_period_end} /><StatCard label="Suspended" value={d.suspended} />
        </div>
      )}
    </AdminShell>
  );
}

/* ---------------- AI Ops ---------------- */
export function AdminAIOps() {
  const [d, setD] = useState(null);
  useEffect(() => { adminService.aiOps().then(setD).catch(() => toast.error("Load failed")); }, []);
  return (
    <AdminShell title="AI Ops" description="RESA usage metadata (no prompt content)">
      {!d ? <Loading /> : (
        <div className="space-y-5" data-testid="admin-ai-ops">
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <StatCard label="Requests" value={d.requests} accent /><StatCard label="Tokens (est)" value={d.total_tokens_estimated.toLocaleString()} />
            <StatCard label="Est. Cost" value={d.estimated_cost} /><StatCard label="Failures" value={d.failures} />
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <Panel title="By model">{Object.entries(d.by_model).map(([k, v]) => <Line key={k} k={k} v={v} />)}</Panel>
            <Panel title="By task">{Object.entries(d.by_task_type).map(([k, v]) => <Line key={k} k={k} v={v} />)}</Panel>
          </div>
          <div className="rounded-xl border border-border bg-white p-4 text-sm">
            <p className="mb-1 font-semibold">Provider configuration (read-only)</p>
            <p className="text-muted-foreground">Default: {d.config.default_provider}/{d.config.default_model} · Fallback: {d.config.fallback_provider}/{d.config.fallback_model} · Prompt v{d.config.prompt_version}</p>
            <p className="mt-1 text-xs text-muted-foreground">{d.note} Secrets and API keys are never shown.</p>
          </div>
        </div>
      )}
    </AdminShell>
  );
}
const Panel = ({ title, children }) => <div className="rounded-xl border border-border bg-white p-4"><p className="mb-2 text-xs font-bold uppercase tracking-wide text-muted-foreground">{title}</p><div className="space-y-1">{children}</div></div>;
const Line = ({ k, v }) => <div className="flex justify-between text-sm"><span className="text-foreground">{k}</span><span className="font-medium text-muted-foreground">{v}</span></div>;

/* ---------------- Feature Flags ---------------- */
export function AdminFlags() {
  const [flags, setFlags] = useState(null);
  const [isSuper, setIsSuper] = useState(false);
  useEffect(() => { adminService.flags().then((d) => setFlags(d.flags)); adminService.context().then((c) => setIsSuper(c.is_super_admin)).catch(() => {}); }, []);
  const toggle = async (key, enabled) => { try { await adminService.setFlag(key, enabled); setFlags((f) => f.map((x) => x.key === key ? { ...x, enabled, overridden: true } : x)); toast.success(`${key} ${enabled ? "enabled" : "disabled"}`); } catch (e) { toast.error(e?.message); } };
  return (
    <AdminShell title="Feature Flags" description="Runtime capability toggles">
      {!flags ? <Loading /> : (
        <div className="divide-y divide-border rounded-xl border border-border bg-white" data-testid="admin-flags">
          {flags.map((f) => (
            <div key={f.key} className="flex items-center justify-between px-4 py-3">
              <div><p className="text-sm font-medium">{f.key}</p>{f.overridden && <p className="text-[10px] text-accent">overridden</p>}</div>
              <Switch checked={f.enabled} disabled={!isSuper} onCheckedChange={(v) => toggle(f.key, v)} data-testid={`flag-${f.key}`} />
            </div>))}
          {!isSuper && <p className="px-4 py-2 text-xs text-muted-foreground">Read-only — Super Admin required to change flags.</p>}
        </div>
      )}
    </AdminShell>
  );
}

/* ---------------- Support Queue (Phase 21) ---------------- */
const T_STATUS = { open: "Open", in_progress: "In Progress", waiting_on_customer: "Waiting on Customer", resolved: "Resolved", closed: "Closed" };
const T_STATUS_CLS = { open: "bg-blue-50 text-blue-700 border-blue-200", in_progress: "bg-amber-50 text-amber-700 border-amber-200", waiting_on_customer: "bg-purple-50 text-purple-700 border-purple-200", resolved: "bg-emerald-50 text-emerald-700 border-emerald-200", closed: "bg-gray-100 text-gray-600 border-gray-200" };
const PRIORITIES = ["low", "normal", "high", "urgent"];
const selCls = "rounded-md border border-border bg-background px-2.5 py-1.5 text-sm focus:border-accent focus:outline-none";

function AdminTicketDetail({ ticketId, onBack }) {
  const [d, setD] = useState(null);
  const [staff, setStaff] = useState([]);
  const [reply, setReply] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => adminService.supportTicket(ticketId).then(setD).catch((e) => toast.error(e?.message)), [ticketId]);
  useEffect(() => { load(); adminService.supportStaff().then((r) => setStaff(r.staff)).catch(() => {}); }, [load]);
  if (!d) return <Loading />;
  const t = d.ticket;
  const act = async (fn) => { setBusy(true); try { const r = await fn(); if (r) setD(r); } catch (e) { toast.error(e?.message); } finally { setBusy(false); } };
  const sendReply = () => act(async () => { const r = await adminService.supportReply(ticketId, reply); setReply(""); toast.success("Reply sent"); return r; });
  const sendNote = () => act(async () => { const r = await adminService.supportInternalNote(ticketId, note); setNote(""); toast.success("Internal note added"); return r; });
  return (
    <div className="space-y-4" data-testid="admin-ticket-detail">
      <button onClick={onBack} className="inline-flex items-center gap-1 text-sm text-accent hover:underline" data-testid="admin-ticket-back"><ArrowLeft className="h-4 w-4" /> Back to queue</button>
      <div className="grid gap-4 lg:grid-cols-3">
        {/* Conversation */}
        <div className="space-y-3 lg:col-span-2">
          <div className="rounded-xl border border-border bg-white p-4">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div><p className="font-brand text-lg font-bold text-brand-navy">{t.subject}</p>
                <p className="text-xs text-muted-foreground">{t.ticket_number} · {t.category_label}</p></div>
              <Badge variant="outline" className={T_STATUS_CLS[t.status]}>{T_STATUS[t.status]}</Badge>
            </div>
            <p className="mt-3 whitespace-pre-wrap rounded-lg bg-secondary/40 p-3 text-sm text-brand-charcoal">{t.description}</p>
          </div>
          <div className="space-y-2" data-testid="admin-ticket-thread">
            {d.messages.map((m) => (
              m.kind === "internal_note" ? (
                <div key={m.id} className="rounded-xl border border-amber-300 bg-amber-50 p-3" data-testid="admin-internal-note">
                  <p className="text-xs font-bold uppercase tracking-wide text-amber-700">Internal note · {m.author_email}</p>
                  <p className="mt-1 whitespace-pre-wrap text-sm text-amber-900">{m.body}</p>
                  <p className="mt-1 text-[11px] text-amber-700/70">{fmtDateTime(m.created_at)}</p>
                </div>
              ) : m.kind === "system_event" ? (
                <p key={m.id} className="text-center text-xs italic text-muted-foreground">{m.body} · {fmtDateTime(m.created_at)}</p>
              ) : (
                <div key={m.id} className={`max-w-[85%] rounded-xl border p-3 ${m.author_role === "staff" ? "ml-auto border-brand-navy/15 bg-brand-navy/5" : "border-border bg-white"}`} data-testid={`admin-msg-${m.author_role}`}>
                  <p className="text-xs font-semibold text-brand-navy">{m.author_role === "staff" ? `${m.author_name} (staff)` : m.author_name || "Customer"}</p>
                  <p className="mt-1 whitespace-pre-wrap text-sm text-brand-charcoal">{m.body}</p>
                  <p className="mt-1 text-[11px] text-muted-foreground">{fmtDateTime(m.created_at)}</p>
                </div>
              )
            ))}
          </div>
          <div className="rounded-xl border border-border bg-white p-3">
            <Textarea rows={3} value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Reply to the customer…" data-testid="admin-reply-input" />
            <div className="mt-2 flex justify-end"><Button onClick={sendReply} disabled={busy || !reply.trim()} className="bg-brand-navy hover:bg-brand-deep-blue" data-testid="admin-reply-send"><Send className="h-4 w-4" /> Send reply</Button></div>
          </div>
          <div className="rounded-xl border border-amber-300 bg-amber-50/40 p-3">
            <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add an internal note (never shown to the customer)…" data-testid="admin-note-input" />
            <div className="mt-2 flex justify-end"><Button variant="outline" onClick={sendNote} disabled={busy || !note.trim()} data-testid="admin-note-add">Add internal note</Button></div>
          </div>
        </div>
        {/* Ops panel */}
        <div className="space-y-4">
          <div className="rounded-xl border border-border bg-white p-4 text-sm" data-testid="admin-ticket-customer">
            <p className="mb-2 text-xs font-bold uppercase tracking-wide text-muted-foreground">Customer</p>
            <Field label="Workspace" value={d.customer.workspace_name} />
            <Field label="Owner" value={d.customer.owner_name} />
            <Field label="Email" value={d.customer.owner_email} />
            <Field label="Subscription" value={d.customer.subscription_status} />
            <Field label="Access" value={d.customer.access} />
            <p className="mt-2 text-[11px] text-muted-foreground">{d.customer.note}</p>
          </div>
          <div className="space-y-3 rounded-xl border border-border bg-white p-4">
            <div><p className="mb-1 text-xs font-semibold text-brand-navy">Status</p>
              <select className={`${selCls} w-full`} value={t.status} onChange={(e) => act(() => adminService.supportSetStatus(ticketId, e.target.value))} data-testid="admin-status-select">
                {Object.entries(T_STATUS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select></div>
            <div><p className="mb-1 text-xs font-semibold text-brand-navy">Priority</p>
              <select className={`${selCls} w-full`} value={t.priority} onChange={(e) => act(() => adminService.supportSetPriority(ticketId, e.target.value))} data-testid="admin-priority-select">
                {PRIORITIES.map((p) => <option key={p} value={p}>{p[0].toUpperCase() + p.slice(1)}</option>)}
              </select></div>
            <div><p className="mb-1 text-xs font-semibold text-brand-navy">Assigned to</p>
              <select className={`${selCls} w-full`} value={t.assigned_to || ""} onChange={(e) => act(() => adminService.supportAssign(ticketId, e.target.value || null))} data-testid="admin-assign-select">
                <option value="">Unassigned</option>
                {staff.map((s) => <option key={s.user_id} value={s.user_id}>{s.email}</option>)}
              </select></div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function AdminSupport() {
  const [metrics, setMetrics] = useState(null);
  const [rows, setRows] = useState(null);
  const [view, setView] = useState("all");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const load = useCallback(() => {
    const p = {};
    if (view !== "all") p.view = view;
    if (q) p.q = q;
    adminService.supportTickets(p).then((d) => setRows(d.tickets)).catch(() => setRows([]));
  }, [view, q]);
  useEffect(() => { if (!selected) load(); }, [load, selected]);
  useEffect(() => { adminService.supportMetrics().then(setMetrics).catch(() => {}); }, [selected]);
  const VIEWS = [["all", "All"], ["open", "Open"], ["in_progress", "In Progress"], ["waiting_on_customer", "Waiting"], ["resolved", "Resolved"], ["urgent", "Urgent"]];
  if (selected) return <AdminShell title="Support" description="Ticket detail"><AdminTicketDetail ticketId={selected} onBack={() => setSelected(null)} /></AdminShell>;
  return (
    <AdminShell title="Support" description="Customer support queue">
      {metrics && (
        <div className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5" data-testid="admin-support-metrics">
          <StatCard label="Open" value={metrics.open} accent />
          <StatCard label="Urgent" value={metrics.urgent} />
          <StatCard label="Waiting on customer" value={metrics.waiting_on_customer} />
          <StatCard label={`Resolved (${metrics.period_days}d)`} value={metrics.resolved_in_period} />
          <StatCard label="Avg first response" value={metrics.avg_first_response || "—"} />
        </div>
      )}
      <div className="mb-4 flex flex-wrap items-center gap-2">
        {VIEWS.map(([v, l]) => (
          <button key={v} onClick={() => setView(v)} data-testid={`admin-support-view-${v}`}
            className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${view === v ? "border-brand-navy bg-brand-navy text-white" : "border-border hover:border-accent"}`}>{l}</button>
        ))}
        <div className="relative ml-auto"><Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ticket #, subject, customer…" className="w-64 pl-8" data-testid="admin-support-search" /></div>
      </div>
      {!rows ? <Loading /> : rows.length === 0 ? <p className="rounded-xl border border-border bg-white p-6 text-center text-sm text-muted-foreground" data-testid="admin-support-empty">No tickets match this view.</p> : (
        <Table head={["Ticket", "Subject", "Customer", "Category", "Priority", "Status", "Updated"]}>
          {rows.map((t) => (
            <tr key={t.id} className="cursor-pointer border-t border-border hover:bg-secondary/40" onClick={() => setSelected(t.id)} data-testid="admin-ticket-row">
              <td className="px-4 py-2.5 font-mono text-xs">{t.ticket_number}</td>
              <td className="px-4 py-2.5 font-medium">{t.subject}</td>
              <td className="px-4 py-2.5 text-xs text-muted-foreground">{t.created_by_email}</td>
              <td className="px-4 py-2.5 text-xs text-muted-foreground">{t.category_label}</td>
              <td className="px-4 py-2.5"><Badge variant="outline" className={t.priority === "urgent" ? "border-brand-burgundy/40 text-brand-burgundy" : "capitalize"}>{t.priority}</Badge></td>
              <td className="px-4 py-2.5"><Badge variant="outline" className={T_STATUS_CLS[t.status]}>{T_STATUS[t.status]}</Badge></td>
              <td className="px-4 py-2.5 text-xs text-muted-foreground">{fmtDateTime(t.updated_at)}</td>
            </tr>))}
        </Table>
      )}
      <SupportAccessPanel />
    </AdminShell>
  );
}

function SupportAccessPanel() {
  const [grants, setGrants] = useState(null);
  const load = useCallback(() => adminService.supportAccess().then((d) => setGrants(d.grants)).catch(() => setGrants([])), []);
  useEffect(() => { load(); }, [load]);
  const revoke = async (id) => { try { await adminService.revokeSupportAccess(id); toast.success("Revoked"); load(); } catch (e) { toast.error(e?.message); } };
  if (!grants || grants.length === 0) return null;
  return (
    <div className="mt-8" data-testid="admin-support-access-panel">
      <p className="mb-2 flex items-center gap-1.5 text-sm font-semibold"><ShieldCheck className="h-4 w-4 text-accent" /> Temporary Support Access</p>
      <Table head={["Workspace", "Granted by", "Reason", "Expires", "Status", ""]}>
        {grants.map((g) => (
          <tr key={g.id} className="border-t border-border">
            <td className="px-4 py-2.5 text-xs text-muted-foreground">{g.workspace_id?.slice(-6)}</td>
            <td className="px-4 py-2.5">{g.granted_by_email}</td>
            <td className="px-4 py-2.5 text-muted-foreground">{g.reason}</td>
            <td className="px-4 py-2.5 text-muted-foreground"><Clock className="mr-1 inline h-3 w-3" />{fmtDateTime(g.expires_at)}</td>
            <td className="px-4 py-2.5"><StatusBadge s={g.status} /></td>
            <td className="px-4 py-2.5">{g.live && <Button size="sm" variant="ghost" className="text-brand-burgundy" onClick={() => revoke(g.id)} data-testid="admin-revoke-support">Revoke</Button>}</td>
          </tr>))}
      </Table>
    </div>
  );
}
const FR_STATUS = { submitted: "Submitted", under_review: "Under Review", planned: "Planned", in_development: "In Development", released: "Released", not_planned: "Not Planned" };
export function AdminFeatureRequests() {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [note, setNote] = useState({});
  const [canSuper, setCanSuper] = useState(false);
  const load = useCallback(() => {
    const p = {}; if (q) p.q = q; if (statusFilter) p.status = statusFilter;
    adminService.featureRequests(p).then((d) => setRows(d.feature_requests)).catch(() => setRows([]));
  }, [q, statusFilter]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { adminService.context().then((c) => setCanSuper(c.permissions?.includes("admin.feedback.manage"))).catch(() => {}); }, []);
  const setStatus = async (id, status) => { try { await adminService.featureSetStatus(id, status); toast.success("Status updated"); load(); } catch (e) { toast.error(e?.message); } };
  const addNote = async (id) => { const body = (note[id] || "").trim(); if (!body) return; try { await adminService.featureNote(id, body); toast.success("Note added"); setNote((n) => ({ ...n, [id]: "" })); load(); } catch (e) { toast.error(e?.message); } };
  const markDup = async (id) => { const canonical = window.prompt("Canonical feature-request ID (leave blank to clear):", ""); try { await adminService.featureDuplicate(id, canonical || null); toast.success("Updated"); load(); } catch (e) { toast.error(e?.message); } };
  return (
    <AdminShell title="Feature Requests" description="Customer product ideas (private, not a public board)">
      <div className="mb-4 flex flex-wrap gap-2">
        <div className="relative"><Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search title or description…" className="w-72 pl-8" data-testid="admin-fr-search" /></div>
        <select className={selCls} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="admin-fr-status-filter">
          <option value="">All statuses</option>
          {Object.entries(FR_STATUS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </div>
      {!rows ? <Loading /> : rows.length === 0 ? <p className="rounded-xl border border-border bg-white p-6 text-center text-sm text-muted-foreground" data-testid="admin-fr-empty">No feature requests yet.</p> : (
        <div className="space-y-3" data-testid="admin-fr-list">
          {rows.map((f) => (
            <div key={f.id} className="rounded-xl border border-border bg-white p-4" data-testid="admin-fr-row">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0"><p className="font-semibold text-brand-navy">{f.title}{f.duplicate_of && <Badge variant="outline" className="ml-2 border-amber-300 text-amber-700">Duplicate</Badge>}</p>
                  <p className="text-xs text-muted-foreground">{f.created_by_email} · {fmtDateTime(f.created_at)}</p></div>
                {canSuper ? (
                  <select className={selCls} value={f.status} onChange={(e) => setStatus(f.id, e.target.value)} data-testid="admin-fr-status">
                    {Object.entries(FR_STATUS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                ) : <Badge variant="outline">{FR_STATUS[f.status]}</Badge>}
              </div>
              <p className="mt-2 text-sm text-brand-charcoal">{f.description}</p>
              {f.benefit && <p className="mt-1 text-xs text-muted-foreground">Why: {f.benefit}</p>}
              {(f.internal_notes || []).length > 0 && (
                <div className="mt-2 space-y-1 rounded-lg bg-amber-50 p-2">
                  {f.internal_notes.map((n, i) => <p key={i} className="text-xs text-amber-900"><span className="font-semibold">{n.author_email}:</span> {n.body}</p>)}
                </div>
              )}
              {canSuper && (
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <Input value={note[f.id] || ""} onChange={(e) => setNote((n) => ({ ...n, [f.id]: e.target.value }))} placeholder="Add internal note…" className="max-w-xs" data-testid="admin-fr-note-input" />
                  <Button variant="outline" size="sm" onClick={() => addNote(f.id)} data-testid="admin-fr-note-add">Add note</Button>
                  <Button variant="ghost" size="sm" onClick={() => markDup(f.id)} data-testid="admin-fr-duplicate">Mark duplicate</Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </AdminShell>
  );
}

/* ---------------- General Feedback (Phase 21) ---------------- */
export function AdminFeedback() {
  const [rows, setRows] = useState(null);
  const [sentiment, setSentiment] = useState("");
  const load = useCallback(() => adminService.feedback(sentiment ? { sentiment } : {}).then((d) => setRows(d.feedback)).catch(() => setRows([])), [sentiment]);
  useEffect(() => { load(); }, [load]);
  const cls = { positive: "border-emerald-200 text-emerald-700", neutral: "border-gray-200 text-gray-600", needs_improvement: "border-amber-200 text-amber-700" };
  const label = { positive: "Positive", neutral: "Neutral", needs_improvement: "Needs Improvement" };
  return (
    <AdminShell title="Feedback" description="General product feedback (no AI sentiment analysis)">
      <div className="mb-4 flex gap-2">
        {[["", "All"], ["positive", "Positive"], ["neutral", "Neutral"], ["needs_improvement", "Needs Improvement"]].map(([v, l]) => (
          <button key={v} onClick={() => setSentiment(v)} data-testid={`admin-feedback-filter-${v || "all"}`}
            className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${sentiment === v ? "border-brand-navy bg-brand-navy text-white" : "border-border hover:border-accent"}`}>{l}</button>
        ))}
      </div>
      {!rows ? <Loading /> : rows.length === 0 ? <p className="rounded-xl border border-border bg-white p-6 text-center text-sm text-muted-foreground" data-testid="admin-feedback-empty">No feedback yet.</p> : (
        <div className="space-y-2" data-testid="admin-feedback-list">
          {rows.map((f) => (
            <div key={f.id} className="rounded-xl border border-border bg-white p-4" data-testid="admin-feedback-row">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <Badge variant="outline" className={cls[f.sentiment]}>{label[f.sentiment]}</Badge>
                <p className="text-xs text-muted-foreground">{f.workspace_name} · {f.created_by_email} · {fmtDateTime(f.created_at)}</p>
              </div>
              <p className="mt-2 text-sm text-brand-charcoal">{f.message}</p>
              {f.module && <p className="mt-1 text-xs text-muted-foreground">Area: {f.module}</p>}
            </div>
          ))}
        </div>
      )}
    </AdminShell>
  );
}

/* ---------------- Announcements ---------------- */
export function AdminAnnouncements() {
  const [title, setTitle] = useState(""); const [body, setBody] = useState(""); const [busy, setBusy] = useState(false); const [confirm, setConfirm] = useState(false);
  const publish = async () => { setBusy(true); try { const r = await adminService.publishAnnouncement(title, body); toast.success(`Published to ${r.recipients} agents`); setTitle(""); setBody(""); setConfirm(false); } catch (e) { toast.error(e?.message); } finally { setBusy(false); } };
  return (
    <AdminShell title="Announcements" description="Broadcast an in-app notice to all agents">
      <div className="max-w-lg space-y-3 rounded-xl border border-border bg-white p-5" data-testid="admin-announcements">
        <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title" data-testid="announcement-title" />
        <Textarea value={body} onChange={(e) => setBody(e.target.value)} placeholder="Message…" rows={4} data-testid="announcement-body" />
        <Button disabled={!title || !body || busy} onClick={() => setConfirm(true)} data-testid="announcement-publish" className="bg-brand-navy hover:bg-brand-deep-blue">Publish announcement</Button>
        <p className="text-xs text-muted-foreground">Delivered as an in-app notification (Phase 18). Email stays in dev/test mode.</p>
      </div>
      <Dialog open={confirm} onOpenChange={setConfirm}>
        <DialogContent><DialogHeader><DialogTitle>Publish to all agents?</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">"{title}" will appear in every active agent's notification center.</p>
          <DialogFooter><Button variant="outline" onClick={() => setConfirm(false)}>Cancel</Button><Button onClick={publish} disabled={busy} data-testid="announcement-confirm">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirm publish"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminShell>
  );
}

/* ---------------- Health ---------------- */
const healthColor = (v) => ({ Healthy: "text-emerald-600", Configured: "text-emerald-600", Degraded: "text-amber-600", "Not Configured": "text-muted-foreground", Error: "text-brand-burgundy", Unknown: "text-muted-foreground" }[v] || "text-muted-foreground");
export function AdminHealth() {
  const [d, setD] = useState(null);
  const load = useCallback(() => adminService.health().then(setD), []);
  useEffect(() => { load(); }, [load]);
  return (
    <AdminShell title="Platform Health" description="Real configuration & connectivity status">
      <Button variant="outline" size="sm" onClick={load} className="mb-4" data-testid="admin-health-refresh"><RefreshCw className="h-4 w-4" /> Refresh</Button>
      {!d ? <Loading /> : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="admin-health">
          {[["API", d.api], ["Database", d.database], [`Stripe (${d.stripe_mode})`, d.stripe], ["Stripe Webhook", d.stripe_webhook], [`Email (${d.email_mode})`, d.email_provider], ["AI Provider", d.ai_provider], ["Storage", d.storage], ["Scheduler", d.scheduler]].map(([k, v]) => (
            <div key={k} className="flex items-center justify-between rounded-xl border border-border bg-white px-4 py-3">
              <span className="text-sm font-medium">{k}</span><span className={`text-sm font-semibold ${healthColor(v)}`}>{v}</span>
            </div>))}
        </div>
      )}
    </AdminShell>
  );
}

/* ---------------- Audit ---------------- */
export function AdminAudit() {
  const [rows, setRows] = useState(null); const [action, setAction] = useState("");
  const load = useCallback(() => adminService.audit(action ? { action } : {}).then((d) => setRows(d.events)), [action]);
  useEffect(() => { load(); }, [load]);
  return (
    <AdminShell title="Audit Logs" description="Append-only record of sensitive actions">
      <Input value={action} onChange={(e) => setAction(e.target.value)} placeholder="Filter by action…" className="mb-4 max-w-xs" data-testid="admin-audit-filter" />
      {!rows ? <Loading /> : rows.length === 0 ? <p className="rounded-xl border border-border bg-white p-6 text-center text-sm text-muted-foreground">No audit events.</p> : (
        <Table head={["When", "Actor", "Action", "Target"]}>
          {rows.map((e) => (
            <tr key={e.id} className="border-t border-border" data-testid="admin-audit-row">
              <td className="px-4 py-2.5 text-muted-foreground">{fmtDateTime(e.created_at)}</td>
              <td className="px-4 py-2.5">{e.actor_email || "—"}</td>
              <td className="px-4 py-2.5 font-medium">{e.action}</td>
              <td className="px-4 py-2.5 text-muted-foreground">{e.target_type || ""} {e.target_id ? e.target_id.slice(-6) : ""}</td>
            </tr>))}
        </Table>
      )}
    </AdminShell>
  );
}

/* ---------------- Security ---------------- */
export function AdminSecurity() {
  const [d, setD] = useState(null);
  useEffect(() => { adminService.security().then(setD).catch(() => toast.error("Load failed")); }, []);
  return (
    <AdminShell title="Security" description="Support sessions & sensitive actions">
      {!d ? <Loading /> : (
        <div className="space-y-6" data-testid="admin-security">
          <div><p className="mb-2 text-sm font-semibold">Active support-access sessions</p>
            {d.active_support_grants.length === 0 ? <p className="rounded-xl border border-border bg-white p-4 text-sm text-muted-foreground">None active.</p> : (
              <Table head={["Workspace", "By", "Reason", "Expires"]}>{d.active_support_grants.map((g) => (
                <tr key={g.id} className="border-t border-border"><td className="px-4 py-2.5 text-xs text-muted-foreground">{g.workspace_id?.slice(-6)}</td><td className="px-4 py-2.5">{g.granted_by_email}</td><td className="px-4 py-2.5 text-muted-foreground">{g.reason}</td><td className="px-4 py-2.5 text-muted-foreground">{fmtDateTime(g.expires_at)}</td></tr>))}</Table>)}
          </div>
          <div><p className="mb-2 text-sm font-semibold">Recent sensitive actions</p>
            {d.recent_sensitive_actions.length === 0 ? <p className="rounded-xl border border-border bg-white p-4 text-sm text-muted-foreground">None recorded.</p> : (
              <Table head={["When", "Actor", "Action"]}>{d.recent_sensitive_actions.map((a) => (
                <tr key={a.id} className="border-t border-border"><td className="px-4 py-2.5 text-muted-foreground">{fmtDateTime(a.created_at)}</td><td className="px-4 py-2.5">{a.actor_email}</td><td className="px-4 py-2.5 font-medium">{a.action}</td></tr>))}</Table>)}
          </div>
          <p className="text-xs text-muted-foreground">{d.note}</p>
        </div>
      )}
    </AdminShell>
  );
}

/* ---------------- Settings ---------------- */
export function AdminSettings() {
  const [ctx, setCtx] = useState(null);
  useEffect(() => { adminService.context().then(setCtx).catch(() => {}); }, []);
  return (
    <AdminShell title="Settings" description="Admin account & platform boundaries">
      <div className="max-w-lg space-y-3 rounded-xl border border-border bg-white p-5 text-sm" data-testid="admin-settings">
        <Field label="Signed in as" value={ctx?.email} />
        <Field label="Platform role" value={ctx?.is_super_admin ? "Super Admin" : "Support Admin"} />
        <Field label="Permissions" value={`${ctx?.permissions?.length || 0} granted`} />
        <p className="text-xs text-muted-foreground">Platform staff are workspace-less and never automatically join a customer workspace. Live Stripe, live email, and Phase 21+ features remain deferred.</p>
      </div>
    </AdminShell>
  );
}
